import { connect } from 'react-redux';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import { withTranslation } from 'react-i18next';

import { actions as orderActions } from 'state/order';
import { selectors as settingsSelectors } from 'state/settings';
import { selectors as errorsSelectors } from 'state/errors';
import { selectors as warningsSelectors } from 'state/warnings';
import { selectors as insuranceSelectors } from 'state/direct-order/insurance';
import { selectors as shipmentSelectors } from 'state/shipments';
import { constants as aditionalOptionsConstants } from 'utils/aditionalOptions';

import keyGenerator from 'utils/keys';
import { isEmpty } from 'utils/types';

import AditionalOption from 'components/summary/AditionalOption/component';

const aditionalOptions = {
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.COMMENT_FOR_TRANSPORTIST]: {
    name: 'COMMENT_FOR_TRANSPORTIST',
    checkboxName: 'HAS_COMMENT_FOR_TRANSPORTIST',
    checkboxLabel: 'aditional_option_comment_for_transportist_field_label',
    checkboxDisabled: () => false,
    fieldType: 'textarea',
    fieldLabel: 'aditional_option_comment_for_transportist_checkbox_label',
    resize: 'vertical',
    height: '96px',
    hasError: (state, shipmentKey, stopType = 'pickup') =>
      !!warningsSelectors.getWarnings(state)[
        keyGenerator.aditionalOptions[stopType]({ shipmentKey })
      ],
    asideRightText: () => null,
    checked: (state, shipmentKey, stopType) =>
      !isEmpty(
        shipmentSelectors.getCommentsForTransportist(
          state,
          shipmentKey,
          stopType
        )
      ),
    value: (state, shipmentKey, stopType) =>
      shipmentSelectors.getCommentsForTransportist(
        state,
        shipmentKey,
        stopType
      ),
    errorMessage: '',
    clarificationText: () =>
      'aditional_option_comment_for_transportist_clarification_text',
    clarificationTextTooltipText:
      'aditional_option_comment_for_transportist_clarification_text_tooltip',
    image: false,
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.INTERNAL_REFERENCE]: {
    name: 'INTERNAL_REFERENCE',
    checkboxName: 'HAS_INTERNAL_REFERENCE',
    checkboxLabel: 'aditional_option_internal_reference_checkbox_label',
    checkboxDisabled: () => false,
    fieldType: 'text',
    fieldLabel: 'aditional_option_internal_reference_field_label',
    hasError: (state, shipmentKey) =>
      !!warningsSelectors.getWarnings(state)[
        keyGenerator.aditionalOptions.internal({ shipmentKey })
      ],
    asideRightText: () => null,
    checked: (state, shipmentKey) =>
      !isEmpty(shipmentSelectors.getInternalReference(state, shipmentKey)),
    value: (state, shipmentKey) =>
      shipmentSelectors.getInternalReference(state, shipmentKey),
    errorMessage: '',
    clarificationText: () =>
      'aditional_option_internal_reference_clarification_text',
    image: false,
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE]: {
    name: 'INSURANCE',
    checkboxName: 'HAS_INSURANCE',
    checkboxLabel: 'aditional_option_insurance_checkbox_label',
    checkboxDisabled: () => false,
    fieldType: 'number',
    fieldLabel: 'aditional_option_insurance_field_label',
    fullWidth: false,
    asideRightText: (state) =>
      settingsSelectors.getCurrency(state).symbol || '',
    hasError: (state) =>
      !!errorsSelectors.getErrors(state)[
        aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE
      ],
    checked: (state) => !!insuranceSelectors.getInsuredQuantity(state),
    value: (state) => insuranceSelectors.getInsuredQuantity(state),
    errorMessage: 'aditional_option_insurance_error_text',
    clarificationText: () => 'aditional_option_insurance_clarification_text',
    image: 'reale.png',
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.APPOINTMENT_INSTRUCTIONS]: {
    name: 'APPOINTMENT_INSTRUCTIONS',
    checkboxName: 'APPOINTMENT_INSTRUCTIONS',
    checkboxLabel: 'additional_option_appointment_instructions_checkbox',
    checkboxDisabled: (state, shipmentKey) =>
      !shipmentSelectors.getShipment(state, shipmentKey)
        .dropoff_appointment_on_demand,
    fieldType: 'textarea',
    fieldLabel: 'additional_option_appointment_instructions_field_label',
    resize: 'vertical',
    height: '96px',
    hasError: (state, shipmentKey) =>
      !!warningsSelectors.getWarnings(state)[
        keyGenerator.aditionalOptions.appointment({ shipmentKey })
      ],
    asideRightText: () => null,
    checked: (state, shipmentKey) =>
      shipmentSelectors.getHasAppointment(state, shipmentKey) &&
      !shipmentSelectors.getShipment(state, shipmentKey)
        .dropoff_appointment_on_demand,
    value: (state, shipmentKey, stopType) =>
      // change for new selector
      shipmentSelectors.getCommentsForTransportist(
        state,
        shipmentKey,
        stopType
      ),
    errorMessage: null,
    clarificationText: (state, shipmentKey) => {
      const { poi_info } = shipmentSelectors.getShipmentAddress(
        state,
        shipmentKey,
        'dropoff'
      );
      return (
        poi_info?.is_booking_required?.shipper_requirements ||
        'additional_option_appointment_instructions_clarification_text'
      );
    },
    clarificationTextTooltipText: false,
    image: false,
  },
};

const enhance = compose(
  withTranslation('Summary'),
  withProps(({ name }) => ({
    ...aditionalOptions[name],
  })),
  connect(
    (state, { name, shipmentKey, stopType }) => ({
      checked: aditionalOptions[name].checked(state, shipmentKey, stopType),
      value: aditionalOptions[name].value(state, shipmentKey, stopType),
      hasError: aditionalOptions[name].hasError(state, shipmentKey, stopType),
      asideRightText: aditionalOptions[name].asideRightText(state, shipmentKey),
      checkboxDisabled: aditionalOptions[name].checkboxDisabled(
        state,
        shipmentKey
      ),
      clarificationText: aditionalOptions[name].clarificationText(
        state,
        shipmentKey
      ),
    }),
    {
      onCheck: orderActions.checkAditionalOption,
      onValueChange: orderActions.setAditionalOption,
    }
  ),
  withStateHandlers(({ checked, focused = false }) => ({ checked, focused }), {
    onCheck:
      (state, { onCheck, name, stopType, shipmentKey }) =>
      (_, { value }) => {
        onCheck({
          type: name,
          stopType,
          shipmentKey,
          value: value ? '' : false,
        });
        return { checked: value };
      },
    onBlur: () => () => ({ focused: false }),

    onFocus: () => () => ({ focused: true }),
  }),
  withHandlers({
    onValueChange:
      ({ onValueChange, stopType, shipmentKey, name }) =>
      (_, { value }) => {
        onValueChange({ type: name, stopType, shipmentKey, value });
      },
  })
);

export default enhance(AditionalOption);
